import Button from '@components/Button/Button';
import { Collapse, CollapseWrapper } from '@components/Collapse';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import Image from '@components/Image';
import ImageContainer from '@components/ImageContainer/ImageContainer';
import { sendSuccess } from '@components/Noti/actions';
import Tip from '@components/Tip';
import Title from '@components/Title';
import { SYS_COMPASS } from '@constants';
import showSceneModal from '@modules/category/SceneModal/showSceneModal';
import selectLibImages from '@modules/mediaLib/utils/selectLibImages';
import MainToggle from '@modules/panel/components/MainToggle';
import SidebarApplyTo from '@modules/sidebar/components/SidebarApplyTo/SidebarApplyTo';
import SidebarHeader from '@modules/sidebar/components/SidebarHeader';
import { $sceneMapApi } from '@stores/dataStores';
import { MainPlayer } from '@stores/krpPlayerStores';
import { $loadedScene } from '@stores/loadedStore';
import {
  ImageType,
  type KrpPlayer,
  type SceneConfigCompass,
  type SceneData,
  SceneType,
} from '@types';
import { useStore } from 'effector-react';
import type { Draft } from 'immer';
// @ts-ignore
import { KrpanoPlayer } from 'pano-sdk';
import { flagToBool, toFlag } from 'pano-utils';
import { useEffect, useRef } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import useHover from 'react-use/lib/useHover';
import { loadScene } from '../player';
import { COMPASS_DEFAULT } from './constants';

const posItems: DropdownItem<SceneConfigCompass['position']>[] = [
  { id: 'topleft', name: '左上角' },
  { id: 'bottom', name: '底部居中' },
];

export function SidebarCompass() {
  const data = useStore($loadedScene)!;
  const { config, id, materialId, north } = data;
  const previewRef = useRef<KrpPlayer | null>(null);

  useEffect(() => {
    MainPlayer.get()?.updateView({ hlookat: north, vlookat: 0 });
    return () => {
      previewRef.current?.removePlayer();
    };
  }, []);

  useEffect(() => {
    previewRef.current?.updateView({ hlookat: north, vlookat: 0 });
  }, [north]);

  useEffect(() => {
    const sceneData: SceneData = JSON.parse(JSON.stringify(data));
    sceneData.view.hLookAt = sceneData.north || 0;
    sceneData.view.vLookAt = 0;
    if (!previewRef.current) {
      const player = new KrpanoPlayer() as KrpPlayer;
      player.createPlayer('pano-view', {}, () => {
        previewRef.current = player;
        player.enableControl(false);
        loadScene({ player, data: sceneData });
      });
    } else {
      loadScene({ player: previewRef.current, data: sceneData });
    }
  }, [id, materialId, config]);

  const handleApplyTo = async () => {
    const { idSet } = await showSceneModal({
      title: '选择要应用到的场景',
      allowedTypes: [SceneType.pano],
      multiple: true,
    });
    if (!idSet) return;
    $sceneMapApi.update((draft) => {
      Array.from(idSet).forEach((_id) => {
        const item = draft.get(_id);
        if (item) {
          item.config.compass = config.compass;
        }
      });
      sendSuccess('指北针设置已应用到所选场景');
    });
  };

  const update = (updater: (draft: Draft<SceneConfigCompass>) => void) => {
    $sceneMapApi.updateScene({
      id,
      updater: (draft) => {
        draft.config.compass ??= { ...COMPASS_DEFAULT };
        updater(draft.config.compass);
      },
    });
  };

  const handleImageSelect = async () => {
    const { libItems, sysItems } = await selectLibImages({
      defaultData: SYS_COMPASS,
    });
    let mediaId = 0;
    let url = '';
    if (libItems) {
      mediaId = libItems[0].id;
      url = libItems[0].url;
    } else if (sysItems) {
      mediaId = 0;
      url = sysItems[0].url;
    }
    if (!url) return;
    update((draft) => {
      draft.mediaId = mediaId;
      draft.mediaUrl = url;
    });
  };

  const onTurnViewClick = () => {
    MainPlayer.get()?.lookTo({
      hlookat: data.north,
      vlookat: 0,
    });
  };

  const [renderPano] = useHover((isHovered) => (
    <div className="relative flex h-100 w-full items-center justify-center">
      <div id="pano-view" className="h-full w-full" />
      {isHovered && (
        <Button
          className="absolute"
          size="sm"
          scheme="gray"
          onClick={onTurnViewClick}
        >
          定位到正北方向
        </Button>
      )}
    </div>
  ));

  const renderImage = () => {
    return (
      <>
        <ImageContainer
          className="h-115"
          placeholder="支持PNG、JPG格式图片"
          // onDeleteClick={handleDelete}
        >
          {!!config.compass?.mediaUrl && (
            <Image
              src={config.compass?.mediaUrl}
              type={
                config.compass?.mediaId ? ImageType.media : ImageType.sysMedia
              }
              className="max-h-full max-w-full"
            />
          )}
        </ImageContainer>
        <div className="mt-10 flex items-center justify-between">
          <Tip>建议尺寸：150x150</Tip>
          <Button size="sm" onClick={handleImageSelect}>
            选择图片
          </Button>
        </div>
        <div className="mt-20 text-sm text-orange">
          注：选择自定义指北针图片时, 请确保图片正上方为正北向
        </div>
      </>
    );
  };

  const renderPosSelect = () => {
    const pos = config.compass?.position ?? COMPASS_DEFAULT.position;
    return (
      <Dropdown
        items={posItems}
        selected={pos}
        onChange={(val) => {
          update((draft) => {
            draft.position = val;
          });
        }}
      />
    );
  };

  return (
    <Scrollbars>
      <CollapseWrapper>
        <SidebarHeader
          title={
            <div className="flex">
              <Title>指北针</Title>
            </div>
          }
        />
        <MainToggle
          isOn={flagToBool(data.config.compass?.enabled ?? 0)}
          onToggle={(bool) => {
            update((draft) => {
              draft.enabled = toFlag(bool);
            });
          }}
        />
        <div
          style={{
            display: flagToBool(data.config.compass?.enabled ?? 0)
              ? 'inline'
              : 'none',
          }}
        >
          <Collapse title="当前正北方向">
            {renderPano}

            <div className="mt-20 text-sm text-orange">
              注：全景图片的正北方向设置将保存到对应素材中,
              并同步更新到其他漫游作品中
            </div>
          </Collapse>
          <Collapse title="指北针样式">{renderImage()}</Collapse>
          <Collapse title="指北针位置">{renderPosSelect()}</Collapse>
        </div>
        <SidebarApplyTo title="把指北针应用到" onClick={handleApplyTo} />
      </CollapseWrapper>
    </Scrollbars>
  );
}
